import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api/useGetQuery";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

const flagsSchema = z.object({
  canClaim: z.boolean(),
  isShiftSignEnabled: z.boolean(),
  isSignOffNotified: z.boolean(),
});

const agentSchema = z.object({
  _id: z.string(),
  phone: z.string(),
  fullName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

const userSchema = z.object({
  _id: z.string(),
  email: z.string(),
  type: z.string(),
  deactivated: z.boolean(),
  tmz: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  flags: flagsSchema,
  agent: agentSchema,
  sendBirdAccessToken: z.string(),
  knockToken: z.string().optional(),
});

export type User = z.infer<typeof userSchema>;

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

/**
 * Fetches the currently authenticated user by their email.
 * The email is determined from the authenticated session context.
 * Results are cached for 5 minutes to reduce the number of requests.
 */
export function useGetUserByEmail(options: UseGetQueryOptions<User> = {}): UseQueryResult<User> {
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/user/getByEmail`,
    responseSchema: userSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_USER_BY_EMAIL_FAILURE,
    },
    staleTime: FIVE_MINUTES_IN_MS,
    ...options,
  });
}
